<script lang="ts" setup>
import { computed, PropType } from 'vue';
import { hash8 } from '@/utils/misc-utils';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';

const userStore = useUserStore();

// --- Component Properties ---
const props = defineProps({
  files: { type: Array as PropType<TaskFileType[]> },
});

export type TaskFileType = {
  name: string;
  path: string;
  type: string;
  fileType: string;
  description: string;
};

const files = computed((): TaskFileType[] => {
  // keep only well-defined files
  let allFiles = props.files || [];
  allFiles = allFiles.filter(
    (file: any): file is TaskFileType =>
      file.name !== undefined && file.name !== null
  );

  // hide debug files for non-developers
  if (!userStore.developer) {
    allFiles = allFiles.filter(
      (file: TaskFileType) => file.type !== 'debug' && file.fileType !== 'log'
    );
  }

  // sort by type [data, report, figure, debug, ...], then name
  const typeOrder = ['data', 'report', 'figure', 'debug'];
  // add the remaining types at the end
  allFiles.forEach((file) => {
    if (!typeOrder.includes(file.type)) {
      typeOrder.push(file.type);
    }
  });

  return allFiles.sort((a, b) => {
    const aTypeIndex = typeOrder.indexOf(a.type);
    const bTypeIndex = typeOrder.indexOf(b.type);
    if (aTypeIndex !== bTypeIndex) {
      return aTypeIndex - bTypeIndex;
    }
    return a.name.localeCompare(b.name);
  });
});

const noFiles = computed(() => {
  return files.value.length === 0;
});

const fields = [
  { key: 'type', label: 'Files', tdClass: 'align-middle text-end min-width' },
  { key: 'name', label: '', tdClass: 'align-middle' },
  { key: 'actions', label: '', tdClass: 'align-middle text-end min-width' },
];

function getFileBadgeVariant(file: TaskFileType): string {
  if (file.fileType === 'directory') {
    return 'secondary';
  } else if (file.type === 'log') {
    return 'secondary';
  } else if (file.type === 'debug') {
    return 'danger';
  } else if (file.type === 'report') {
    return 'primary';
  } else if (file.type === 'figure') {
    return 'info';
  } else if (file.type === 'data') {
    return 'warning';
  }

  // fallback icon
  return 'secondary';
}

function getFileIcon(file: TaskFileType): [string[], string] {
  if (file.fileType === 'directory') {
    return [['bi', 'bi-folder'], ''];
  } else if (file.fileType === 'log') {
    return [['bi', 'bi-file-earmark-text'], ''];
  } else if (file.fileType === 'pdf') {
    //return [['material-symbols-outlined'], 'picture_as_pdf'];
    //return [['bi', 'bi-filetype-pdf'], ''];
    return [['bi', 'bi-file-earmark-pdf'], ''];
  } else if (file.fileType === 'html') {
    //return [['material-symbols-outlined'], 'HTML'];
    //return [['bi', 'bi-filetype-html'], ''];
    //return [['far', 'fa-file-code'], ''];
    return [['bi', 'bi-file-earmark-text'], ''];
  } else if (file.fileType === 'zip') {
    return [['bi', 'bi-archive'], ''];
  } else if (file.type === 'figure') {
    return [['bi', 'bi-graph-up'], ''];
  } else if (file.type === 'data') {
    return [['bi', 'bi-archive'], ''];
  } else if (file.type === 'report') {
    return [['bi', 'bi-file-earmark-text'], ''];
  } else if (file.type === 'debug') {
    return [['bi', 'bi-file-earmark-code'], ''];
  }

  // fallback icon
  return [['bi', 'bi-file-earmark'], ''];
}

function filesTableRowClicked(file: TaskFileType) {
  if (file.fileType === 'directory') {
    emits('directory-info-requested', file);
  } else {
    emits('file-preview-requested', file);
  }
}

// --- Emits ---
const emits = defineEmits<{
  (e: 'directory-info-requested', file: TaskFileType): void;
  (e: 'directory-view-requested', file: TaskFileType): void;
  (e: 'file-preview-requested', file: TaskFileType): void;
  (e: 'file-download-requested', file: TaskFileType): void;
}>();
</script>
<template>
  <div>
    <BAlert v-model="noFiles" class="mb-0" variant="secondary" size="sm">
      <small>No files available.</small>
    </BAlert>
    <BTable
      v-if="!noFiles"
      :items="files"
      :fields="fields"
      small
      striped
      hover
      responsive="sm"
      stacked="sm"
      class="browser-table mb-0"
      @row-clicked="filesTableRowClicked"
    >
      <!-- file type -->
      <template #cell(type)="{ item }">
        <!-- show the file icon in badge with 50% transparency -->
        <b-badge :variant="getFileBadgeVariant(item)" class="file-badge">
          <i :class="getFileIcon(item)[0]">
            {{ getFileIcon(item)[1] }}
          </i>
          {{ item.type.toLowerCase() }}/{{ item.fileType.toLowerCase() }}
        </b-badge>
      </template>
      <!-- file name, bold for items of `data` type -->
      <template #cell(name)="{ item }">
        <span>
          {{ item.name }}
        </span>
        <!-- show description icon with tooltip -->
        <template v-if="item.description">
          <font-awesome-icon
            class="ms-2 text-secondary"
            icon="question-circle"
            :id="`file-description-${hash8(item.path)}`"
          />
          <b-tooltip
            :target="`file-description-${hash8(item.path)}`"
            triggers="hover"
            placement="top"
            class="large-tooltip"
          >
            {{ item.description }}
          </b-tooltip>
        </template>
      </template>
      <!-- show the file actions -->
      <template #cell(actions)="{ item }">
        <b-button-group size="sm">
          <b-button
            v-if="item.fileType === 'directory'"
            @click="emits('directory-info-requested', item)"
            variant="outline-secondary"
            v-b-tooltip.hover
            title="Show details"
          >
            <font-awesome-icon icon="info-circle" />
          </b-button>
          <b-button
            v-if="item.fileType === 'directory'"
            @click="emits('directory-view-requested', item)"
            variant="outline-secondary"
            v-b-tooltip.hover
            title="Browse directory"
          >
            <font-awesome-icon icon="folder-open" />
          </b-button>
          <b-button
            v-if="item.fileType !== 'directory'"
            @click="emits('file-preview-requested', item)"
            variant="outline-secondary"
            v-b-tooltip.hover
            title="Show details"
          >
            <font-awesome-icon icon="info-circle" />
          </b-button>
          <b-button
            v-if="item.fileType !== 'directory'"
            @click="emits('file-download-requested', item)"
            variant="outline-secondary"
            v-b-tooltip.hover
            title="Download"
          >
            <font-awesome-icon icon="download" />
          </b-button>
        </b-button-group>
      </template>
    </BTable>
  </div>
</template>
<style scoped>
/* make the file badge slightly transparent, but fully opaque on hover over the table row */
.file-badge {
  opacity: 0.75;
}
.browser-table tbody tr:hover .file-badge {
  opacity: 1;
}
</style>
